import type { CreditDeclineInfo } from '@/shared/types/credit-decline';

export type { CreditDeclineInfo } from '@/shared/types/credit-decline';

export type PaymentOption = {
  offerId: string;
  tenure: number;
  productType?: string | null;
  downPaymentAmount: {
    amount: number;
    currency: string;
  };
  processingFeeAmount: {
    amount: number;
    currency: string;
  };
  instalmentAmount: {
    amount: number;
    currency: string;
  };
  principalAmount: {
    amount: number;
    currency: string;
  };
  termCostAmount: {
    amount: number;
    currency: string;
  };
  totalToBeRepaidAmount: {
    amount: number;
    currency: string;
  };
  totalCustomerFeeAmount: {
    amount: number;
    currency: string;
  };
  averageMonthlyFee?: {
    amount: number;
    currency: string;
  };
  schedulePreview: {
    sequence: number;
    dueNow: boolean;
    dueDate: string;
    amount: {
      amount: number;
      currency: string;
    };
  }[];
  subvention?: {
    applied: boolean;
    appliedTotalAmount: {
      amount: number;
      currency: string;
    };
    bySkuBreakdown: {
      sku: string;
      tamaraSubventedAmount: {
        amount: number;
        currency: string;
      };
      merchantSubventedAmount: {
        amount: number;
        currency: string;
      };
    }[];
    campaignId: string;
    rejectionCode: string | null;
    subventionBreakdown: {
      campaignSubvention: {
        source: string;
        subventionRate: number;
        refundRate: number;
        subventedAmount: {
          amount: number;
          currency: string;
        };
      };
      tamaraSubvention: {
        source: string;
        subventionRate: number;
        refundRate: number;
        subventedAmount: {
          amount: number;
          currency: string;
        };
      } | null;
    };
    totalSubventionRate: number;
  } | null;
  waivedOffFee?: {
    amount: number;
    currency: string;
  } | null;
  waivedOffReason?: string | null;
  isFeeWaivedOff?: boolean;
  needsDocumentSigning: boolean;
  equalDownPaymentStrategy: boolean | null;
  preSelected?: boolean | null;
  annualPercentageRate?: number | null;
  firstRepaymentDate?: string | null;
  appliedRewards?: {
    rewardAmount?: {
      amount: number;
      currency?: Currency | string;
    };
  };
  cashback?: {
    actualAmount?: {
      amount: number;
      currency?: Currency | string;
    };
  };
  /** Merchandising traits returned by the backend for this option (e.g. lowest market price). */
  traits?: string[] | null;
};

/** Backend merchandising traits attached to a {@link PaymentOption}. */
export enum PaymentOptionTrait {
  LOWEST_MARKET_PRICE = 'LOWEST_MARKET_PRICE',
}

/** Legal / disclosure document returned on the checkout session (`documents`). */
export type LoanDocument = {
  id: string;
  documentItemType: string;
  url: string;
  /** Backend flags such as REQUIRES_CUSTOMER_SIGNING — may be null or omitted. */
  attributes?: string[] | null;
};

/**
 * Early repayment payload (FDS prevention flow).
 * Backend sends this alongside `status: AWAIT_EARLY_REPYAYMENT_COMPLETION`
 * (note: backend typo on REPYAYMENT is intentional, must match wire format)
 * when the customer must clear an overdue installment to continue checkout.
 */
export type EarlyRepayment = {
  paymentId: string;
  amount: {
    amount: number;
    currency: string;
  };
  orderId: string;
  merchantId: string;
  merchantName: string;
  merchantLogoUrl: string;
  orderReference: string;
  dueDate: string;
  installmentIndex: number;
  /** Present on overdue repayment line items. */
  daysOverdue?: number;
};

/**
 * Cosmos overdue payload — mirrors checkout-service `OverduePaymentsDTO`
 * (see checkout-service MR !364).
 */
export type OverduePayments = {
  hasOverdue: boolean;
  maxDaysOverdue: number;
  totalDueAmount: {
    amount: number;
    currency: string;
  };
  count: number;
  /** Line items use the same shape as `InstallmentToRepayDTO` / early repayment. */
  payments: EarlyRepayment[];
};

export type CheckoutSession = {
  id: string;
  country: CountryData;
  orderId: string;
  orderTotalAmount: {
    amount: number;
    currency: string;
  };
  paymentType:
    | "PAY_BY_INSTALMENTS"
    | "PAY_NOW"
    | "PAY_NEXT_MONTH"
    | "PAY_BY_LATER"
    | "PAY_BY_FINANCE";
  status: CheckoutStatus;
  merchant: {
    id: string;
    name: string;
    arName: string;
    platform: string;
    logoUrl: string;
    urls: {
      success: string;
      failure: string;
      cancel: string;
    };
  };
  order: {
    id: string;
    items: CartItem[];
    currency: Currency;
    countryCode: string;
  };
  customerId: string;
  paymentOptions: PaymentOption[];
  selectedPaymentOptionId: string;
  /** True when the offer was pre-selected upstream → render single page checkout. */
  isOfferPreSelected: boolean;
  locale: string;
  installments: number | null;
  ivrStatus: string;
  /** Canonical list (BE field `documents`; legacy `loan_documents` merged at parse time). */
  documents: LoanDocument[];
  paymentMethod: PaymentMethod | null;
  paymentDetails?: {
    availableWalletBalance?: {
      amount: number;
      currency: string;
    };
    cardDownPaymentAmountWithWallet?: {
      amount: number;
      currency: string;
    };
    walletDownPaymentAmount?: {
      amount: number;
      currency: string;
    };
    canWalletCoverDownPayment: boolean;
  };
  error?: SessionErrorData;
  paymentResult: PaymentResult;
  shouldShowIvr: boolean;
  shouldShowDocumentSigning: boolean;
  ivrTimeout: number;
  declinedOffers: DeclinedOffer[];
  /** Present when status is AWAIT_EARLY_REPYAYMENT_COMPLETION (FDS prevention flow). */
  earlyRepayment?: EarlyRepayment | null;
  /** Present when multiple overdue installments must be settled (pay-overdue flow). */
  overduePayments?: OverduePayments | null;
  /** Outbound external verification URL (KYC / questionnaire) — not 3DS. */
  redirectUrl?: string;
  /** Credit decline explainer payload; when present, render decline UI. */
  creditDeclineInfo?: CreditDeclineInfo | null;
};

/** Matches backend declined_offers item (camelized). */
export type DeclinedOffer = {
  orderId?: string;
  customerId?: string;
  merchantId?: string;
  offerId: string;
  reason?: string;
  tenure: number;
  subvention?: {
    applied?: boolean;
    campaignId?: string;
    rejectionCode?: string;
    subventionBreakdown?: Record<string, unknown>;
    bySkuBreakdown?: Record<string, unknown>[];
    campaignBreakdown?: Record<string, unknown>[];
  } | null;
};

export type PaymentResult = {
  redirectUrl: string;
  status: PaymentResultStatus;
  errorMessage?: string;
  errorCode?: string;
};

export type SessionErrorData = {
  type: SessionError;
  message: string;
  title?: string;
  code?: string;
};

export enum PaymentResultStatus {
  UNKNOWN,
  DECLINED,
  DENIED,
  PENDING,
}

import type { LocalizedIconType } from "@/shared/types/localized-icon";

export { LOCALIZED_ICON_TYPE } from "@/shared/types/localized-icon";
export type { LocalizedIconType };

export enum Page {
  PAYMENT_PLAN_SELECTION,
  IVR_CONFIRMATION,
  PAYMENT_CONFIRMATION,
  SUCCESS,
  EARLY_REPAYMENT,
}

export type RenderAmountFunc = (amount: number | string) => React.ReactNode;

export type NavigateFunc = (page: Page) => void;

export type GetCheckoutSessionFunc = () => Promise<CheckoutSession>;

export type TranslateFunc = (
  key: string,
  options?: Record<string, unknown>,
) => string;

export type AppContext = {
  session: CheckoutSession;
  rtl: boolean;
  localizedIcon: (icon: LocalizedIconType) => React.ReactNode;
  amount: RenderAmountFunc;
  navigate: NavigateFunc;
  getCheckoutSession: GetCheckoutSessionFunc;
  t: TranslateFunc;
  displayDate: (date: string) => string;
  store: Store;
  applePay: {
    deviceSupports: () => boolean;
  };
  configuration: CheckoutSessionConfiguration;
  selectedPayment: () => {
    paymentOption: PaymentOption | null;
    amountToPayNow: number;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendEvent: (e: AppEvent, data?: Record<string, any>) => void;
  sendMobileMessage: SendMobileMessageFunc;
};

export type SendMobileMessageFunc = (source: string, action: string) => void;

export enum Currency {
  SAR,
  AED,
  KWD,
  QAR,
  BHD,
  OMR,
}

export enum PaymentMethod {
  KNET_CARD,
  APPLE_PAY,
  CREDIT_CARD,
  MADA_CARD,
  TAMARA_PAY_CARD,
  BANK_TRANSFER,
}

export enum CheckoutStatus {
  UNKNOWN = "UNKNOWN",
  NEW = "NEW",
  PRE_UNDERWRITING_FRAUD_CHECK = "PRE_UNDERWRITING_FRAUD_CHECK",
  PRE_UDNERWRITING_FRAUD_CHECK = "PRE_UDNERWRITING_FRAUD_CHECK",
  EVALUATING_ORDER_CREDIT = "EVALUATING_ORDER_CREDIT",
  ORDER_CREDIT_EVALUATED = "ORDER_CREDIT_EVALUATED",
  AWAIT_PAYMENT_OPTION_SELECTION = "AWAIT_PAYMENT_OPTION_SELECTION",
  AWAIT_PAYMENT_METHOD_SELECTION = "AWAIT_PAYMENT_METHOD_SELECTION",
  VALIDATING_PAYMENT_OPTION_SELECTION = "VALIDATING_PAYMENT_OPTION_SELECTION",
  PAYMENT_OPTION_SELECTED = "PAYMENT_OPTION_SELECTED",
  PAYMENT_OPTION_VALIDATED = "PAYMENT_OPTION_VALIDATED",
  VALIDATING_PAYMENT_METHOD_SELECTION = "VALIDATING_PAYMENT_METHOD_SELECTION",
  PAYMENT_METHOD_SELECTED = "PAYMENT_METHOD_SELECTED",
  AWAIT_IVR_CONFIRMATION = "AWAIT_IVR_CONFIRMATION",
  IVR_CONFIRMED = "IVR_CONFIRMED",
  AWAIT_DOCUMENT_SIGNING = "AWAIT_DOCUMENT_SIGNING",
  FETCHING_DOCUMENTS = "FETCHING_DOCUMENTS",
  APPROVING_CONTRACT = "APPROVING_CONTRACT",
  CHECKING_FRAUD = "CHECKING_FRAUD",
  SCREENING_CHECK = "SCREENING_CHECK",
  BASIC_FRAUD_CHECK = "BASIC_FRAUD_CHECK",
  SECOND_FRAUD_CHECK = "SECOND_FRAUD_CHECK",
  PROCESSING_SECOND_FRAUD_CHECK = "PROCESSING_SECOND_FRAUD_CHECK",
  CARD_PROCESSING_SUCCEEDED = "CARD_PROCESSING_SUCCEEDED",
  CARD_PROCESSING_FAILED = "CARD_PROCESSING_FAILED",
  AWAIT_BANK_INFO_STEP = "AWAIT_BANK_INFO_STEP",
  THIRD_FRAUD_CHECK = "THIRD_FRAUD_CHECK",
  PROCESSING_THIRD_FRAUD_CHECK = "PROCESSING_THIRD_FRAUD_CHECK",
  RESERVE_DOWNPAYMENT_BALANCE = "RESERVE_DOWNPAYMENT_BALANCE",
  INITIALIZE_CARD_PAYMENT = "INITIALIZE_CARD_PAYMENT",
  PROCESSING_CARD_PAYMENT = "PROCESSING_CARD_PAYMENT",
  CHECKING_QUESTIONNAIRE_REQUIRED = "CHECKING_QUESTIONNAIRE_REQUIRED",
  CHECKING_OVERDUE_PAYMENTS = "CHECKING_OVERDUE_PAYMENTS",
  AWAIT_QUESTIONNAIRE_COMPLETION = "AWAIT_QUESTIONNAIRE_COMPLETION",
  CHECKING_IDV_ELIGIBILITY = "CHECKING_IDV_ELIGIBILITY",
  AWAIT_IDV_COMPLETION = "AWAIT_IDV_COMPLETION",
  VERIFYING_IDV_RESULT = "VERIFYING_IDV_RESULT",
  AWAIT_EARLY_REPYAYMENT_COMPLETION = "AWAIT_EARLY_REPYAYMENT_COMPLETION",
  EARLY_REPAYMENT_COMPLETED = "EARLY_REPAYMENT_COMPLETED",
  FINISHED = "FINISHED",
  SUCCEEDED = "SUCCEEDED",
  FAILED = "FAILED",
  INVALIDATED = "INVALIDATED",
}

export const CHECKOUT_STATUS_PROCESSING_PAYMENT: CheckoutStatus[] = [
  CheckoutStatus.ORDER_CREDIT_EVALUATED,
  // Transient states the BE passes through between the user submitting a
  // payment method and the fraud checks starting. Without them the app treats
  // the payment as no longer in flight, stops polling, and strands the user on
  // the payment form while the BE goes on to approve the order.
  CheckoutStatus.VALIDATING_PAYMENT_METHOD_SELECTION,
  CheckoutStatus.PAYMENT_METHOD_SELECTED,
  CheckoutStatus.FETCHING_DOCUMENTS,
  CheckoutStatus.CHECKING_FRAUD,
  CheckoutStatus.SCREENING_CHECK,
  CheckoutStatus.BASIC_FRAUD_CHECK,
  CheckoutStatus.SECOND_FRAUD_CHECK,
  CheckoutStatus.PROCESSING_SECOND_FRAUD_CHECK,
  CheckoutStatus.THIRD_FRAUD_CHECK,
  CheckoutStatus.PROCESSING_THIRD_FRAUD_CHECK,
  CheckoutStatus.INITIALIZE_CARD_PAYMENT,
  CheckoutStatus.PROCESSING_CARD_PAYMENT,
  CheckoutStatus.RESERVE_DOWNPAYMENT_BALANCE,
  CheckoutStatus.CARD_PROCESSING_SUCCEEDED,
  CheckoutStatus.CARD_PROCESSING_FAILED,
  CheckoutStatus.AWAIT_BANK_INFO_STEP,
];

export enum SessionError {
  NONE,
  UNKNOWN,
  NOT_FOUND,
}

export type CartItem = {
  id: string;
  referenceId: string;
  type: string;
  name: string;
  sku: string;
  quantity: number;
  taxAmount: {
    amount: number;
    currency: string;
  };
  totalAmount: {
    amount: number;
    currency: string;
  };
  unitPrice: {
    amount: number;
    currency: string;
  };
  discountAmount: {
    amount: number;
    currency: string;
  };
  imageUrl: string;
  itemUrl: string;
};

export type Store = {
  get: (key: string) => Promise<string | null>;
  set: (key: string, value: string) => Promise<void>;
};

export type CkoHubVersion = "unknown" | "v1" | "v2";

export type CheckoutSessionConfiguration = {
  applePayEnabled: boolean;
  pcidssEnabled: boolean;
  pcidssApplePayEnabled: boolean;
  ckoHubVersion: CkoHubVersion;
};

export enum CardScheme {
  UNKNOWN,
  VISA,
  MASTERCARD,
  AMEX,
  MADA,
  KNET,
}

export enum StoredCardStatus {
  UNKNOWN,
  ACTIVE,
}

export enum PaymentType {
  PAY_NEXT_MONTH = "PAY_NEXT_MONTH",
  PAY_BY_INSTALMENTS = "PAY_BY_INSTALMENTS",
  PAY_NOW = "PAY_NOW",
  PAY_BY_LATER = "PAY_BY_LATER",
  PAY_BY_FINANCE = "PAY_BY_FINANCE",
}

export enum CardTokenType {
  UNKNOWN,
  CARD,
  PREPAID,
}

export type StoredCard = {
  id: number;
  scheme: CardScheme;
  schemeLocal: CardScheme;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  status: StoredCardStatus;
  paymentMethod: PaymentMethod;
  isExpiredSoon: Set<PaymentType>;
  isDebit: boolean;
  isPrepaid: boolean;
  isAutoCharge: boolean;
  isBinBlacklisted: boolean;
  bin: string;
  isDisabledCvv: boolean;
  cardPriority: number;
  tokenType: CardTokenType;
  fingerprint?: string;
};

export type AppEvent = {
  name: string;
};

export type CountryData = {
  currency: Currency;
  ckoPublicKey: string;
  ckoPublicKeyV2: string;
  cardsAllowed: CardScheme[];
  applePayAllowed: boolean;
  isoCode2: string;
  isoCode3: string;
};

export type CardData = {
  number: string;
  expiryMonth: number;
  expiryYear: number;
  cvv: string;
};

export type CardForm = {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  saveCard: boolean;
};
